import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { AlertsPanel } from './AlertsPanel';
import { NamespaceDashboard } from './NamespaceDashboard';
import type { KubernetesConsoleApi } from './api';
import type { K8sObject } from './types';

const SAVED_QUERIES_KEY = 'k8sConsole.savedPromqlQueries';
const QUICK_START_KEY = 'k8sConsole.quickStartProgress';
const PANEL = {
  border: '1px solid #4f5255',
  borderRadius: 2,
  bgcolor: '#1f1f1f',
  p: 2,
};

interface ResourceContext {
  kind: string;
  resource: string;
  apiBase: string;
  object: K8sObject;
}

interface PrometheusSeries {
  metric?: Record<string, string>;
  values?: [number, string][];
  value?: [number, string];
}

const QUICK_STARTS = [
  {
    id: 'rollout-debug',
    title: 'Debug an unhealthy rollout',
    description: 'Check rollout health, ReplicaSets, pods, alerts and logs.',
    steps: [
      'Confirm the workload generation and ready replica counts.',
      'Inspect the newest ReplicaSet and its pod status.',
      'Review warning events and recent container logs.',
      'Ask Nora to correlate the evidence before changing the rollout.',
    ],
  },
  {
    id: 'pvc-recovery',
    title: 'Recover a persistent volume claim',
    description: 'Verify binding, capacity, mount failures and safe recovery.',
    steps: [
      'Inspect PVC and PersistentVolume phase and storage class.',
      'Find pods mounting the claim and review warning events.',
      'Check namespace storage quotas and node volume conditions.',
      'Prepare a recovery plan and validate data-retention impact.',
    ],
  },
  {
    id: 'certificate-renewal',
    title: 'Investigate certificate renewal',
    description: 'Trace certificate, issuer, secret and controller events.',
    steps: [
      'Inspect Certificate readiness and renewal timestamps.',
      'Open the referenced Issuer or ClusterIssuer.',
      'Check challenge/order resources and controller events.',
      'Verify the resulting TLS secret before rotating consumers.',
    ],
  },
  {
    id: 'namespace-onboarding',
    title: 'Onboard a namespace',
    description: 'Validate access, quotas, limits, networking and GitOps.',
    steps: [
      'Confirm namespace access and required RBAC verbs.',
      'Review ResourceQuota and LimitRange defaults.',
      'Verify default NetworkPolicies and route ingress.',
      'Connect catalog ownership and the Argo CD application.',
    ],
  },
];

function JsonPanel({ value }: { value: unknown }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        maxHeight: 460,
        overflow: 'auto',
        borderRadius: 1,
        bgcolor: '#101010',
        color: '#d8dee9',
        fontSize: 11,
        whiteSpace: 'pre-wrap',
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Box>
  );
}

function seriesPoints(series: PrometheusSeries): [number, number][] {
  const values = series.values ?? (series.value ? [series.value] : []);
  return values
    .map(([timestamp, value]) => [timestamp, Number(value)] as [number, number])
    .filter(([, value]) => Number.isFinite(value));
}

function PrometheusGraph({ series }: { series: PrometheusSeries[] }) {
  const lines = useMemo(() => {
    const all = series.flatMap(seriesPoints);
    if (!all.length) return [];
    const minX = Math.min(...all.map(point => point[0]));
    const maxX = Math.max(...all.map(point => point[0]));
    const minY = Math.min(...all.map(point => point[1]));
    const maxY = Math.max(...all.map(point => point[1]));
    return series.map((entry, index) => ({
      color: ['#8bc1f7', '#92c55e', '#f4c145', '#b2a3ff', '#f0561d'][index % 5],
      label:
        Object.entries(entry.metric ?? {})
          .map(([key, value]) => `${key}=${value}`)
          .join(', ') || `Series ${index + 1}`,
      points: seriesPoints(entry)
        .map(
          ([x, y]) =>
            `${20 + ((x - minX) / Math.max(1, maxX - minX)) * 760},${
              210 - ((y - minY) / Math.max(1e-12, maxY - minY)) * 180
            }`,
        )
        .join(' '),
    }));
  }, [series]);
  if (!lines.length) {
    return (
      <Typography sx={{ color: '#c7c7c7', fontSize: 12 }}>
        The query returned no numeric samples.
      </Typography>
    );
  }
  return (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <svg width="800" height="230" role="img" aria-label="Prometheus result">
          <line x1="20" y1="210" x2="780" y2="210" stroke="#4f5255" />
          <line x1="20" y1="30" x2="20" y2="210" stroke="#4f5255" />
          {lines.map(line => (
            <polyline
              key={line.label}
              points={line.points}
              fill="none"
              stroke={line.color}
              strokeWidth="2"
            />
          ))}
        </svg>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {lines.map(line => (
          <Chip
            key={line.label}
            size="small"
            label={line.label}
            sx={{ color: line.color, borderColor: line.color }}
            variant="outlined"
          />
        ))}
      </Stack>
    </>
  );
}

function PromqlPanel({
  api,
  cluster,
  namespace,
}: {
  api: KubernetesConsoleApi;
  cluster: string;
  namespace: string;
}) {
  const [query, setQuery] = useState(
    `sum(rate(container_cpu_usage_seconds_total{namespace="${namespace}",container!=""}[5m])) by (pod)`,
  );
  const [minutes, setMinutes] = useState(60);
  const [result, setResult] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string[]>(() => {
    try {
      const value = JSON.parse(localStorage.getItem(SAVED_QUERIES_KEY) ?? '[]');
      return Array.isArray(value)
        ? value.filter(item => typeof item === 'string')
        : [];
    } catch {
      return [];
    }
  });

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const end = Math.floor(Date.now() / 1000);
      setResult(
        await api.prometheusQuery(cluster, {
          query,
          start: end - minutes * 60,
          end,
          step: Math.max(15, Math.ceil((minutes * 60) / 600)),
        }),
      );
    } catch (runError) {
      setError(runError instanceof Error ? runError.message : String(runError));
    } finally {
      setLoading(false);
    }
  };
  const series: PrometheusSeries[] =
    result?.data?.result ??
    result?.result?.data?.result ??
    result?.result ??
    [];
  return (
    <Stack spacing={2}>
      <Box sx={PANEL}>
        <Stack spacing={1.5}>
          <TextField
            label="PromQL"
            multiline
            minRows={3}
            value={query}
            onChange={event => setQuery(event.target.value)}
            fullWidth
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <TextField
              select
              size="small"
              label="Time range"
              value={minutes}
              onChange={event => setMinutes(Number(event.target.value))}
              sx={{ width: 150 }}
            >
              {[15, 60, 360, 1440, 10080].map(value => (
                <MenuItem key={value} value={value}>
                  {value < 60
                    ? `${value} minutes`
                    : value < 1440
                    ? `${value / 60} hours`
                    : `${value / 1440} days`}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              onClick={run}
              disabled={loading || !query.trim()}
            >
              Run query
            </Button>
            <Button
              variant="outlined"
              disabled={!query.trim() || saved.includes(query)}
              onClick={() => {
                const next = [query, ...saved].slice(0, 20);
                setSaved(next);
                localStorage.setItem(SAVED_QUERIES_KEY, JSON.stringify(next));
              }}
            >
              Save query
            </Button>
          </Stack>
          {saved.length > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap">
              {saved.map(item => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  onClick={() => setQuery(item)}
                  onDelete={() => {
                    const next = saved.filter(entry => entry !== item);
                    setSaved(next);
                    localStorage.setItem(
                      SAVED_QUERIES_KEY,
                      JSON.stringify(next),
                    );
                  }}
                  sx={{ maxWidth: 360 }}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {loading && <CircularProgress size={24} />}
      {result && (
        <Box sx={PANEL}>
          <PrometheusGraph series={series} />
        </Box>
      )}
    </Stack>
  );
}

function QuickStartsPanel({
  api,
  cluster,
  namespace,
  resource,
}: {
  api: KubernetesConsoleApi;
  cluster: string;
  namespace: string;
  resource?: ResourceContext;
}) {
  const [active, setActive] = useState<string>();
  const [step, setStep] = useState(0);
  const [preflight, setPreflight] = useState<unknown>();
  const selected = QUICK_STARTS.find(item => item.id === active);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(QUICK_START_KEY) ?? '{}');
      if (typeof saved.id === 'string' && Number.isInteger(saved.step)) {
        setActive(saved.id);
        setStep(saved.step);
      }
    } catch {
      // Invalid local progress is ignored.
    }
  }, []);
  useEffect(() => {
    if (!active) return;
    localStorage.setItem(QUICK_START_KEY, JSON.stringify({ id: active, step }));
  }, [active, step]);

  if (selected) {
    return (
      <Box sx={PANEL}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#f0f0f0', fontWeight: 700 }}>
                {selected.title}
              </Typography>
              <Typography sx={{ color: '#c7c7c7', fontSize: 12 }}>
                Step {step + 1} of {selected.steps.length}
              </Typography>
            </Box>
            <Button
              onClick={() => {
                setActive(undefined);
                setStep(0);
                setPreflight(undefined);
                localStorage.removeItem(QUICK_START_KEY);
              }}
            >
              Exit
            </Button>
          </Stack>
          <Divider />
          <Typography sx={{ color: '#f0f0f0' }}>
            {selected.steps[step]}
          </Typography>
          {preflight !== undefined && <JsonPanel value={preflight} />}
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={step === 0}
              onClick={() => setStep(value => Math.max(0, value - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              onClick={async () =>
                setPreflight(
                  await api.quickStartPreflight(
                    cluster,
                    namespace,
                    selected.id,
                  ),
                )
              }
            >
              Run preflight
            </Button>
            <Button
              variant="contained"
              disabled={step === selected.steps.length - 1}
              onClick={() =>
                setStep(value => Math.min(selected.steps.length - 1, value + 1))
              }
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 2,
      }}
    >
      {QUICK_STARTS.map(item => (
        <Box key={item.id} sx={PANEL}>
          <Typography sx={{ color: '#f0f0f0', fontWeight: 700 }}>
            {item.title}
          </Typography>
          <Typography sx={{ color: '#c7c7c7', fontSize: 12, my: 1 }}>
            {item.description}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              setActive(item.id);
              setStep(0);
              setPreflight(undefined);
            }}
          >
            Start
          </Button>
        </Box>
      ))}
    </Box>
  );
}

export function OperationsCenter({
  api,
  cluster,
  namespace,
  resource,
  tab,
  onTabChange,
}: {
  api: KubernetesConsoleApi;
  cluster: string;
  namespace: string;
  resource?: ResourceContext;
  /** Owned by the page so the open tab is part of the URL. */
  tab: string;
  onTabChange: (tab: string) => void;
}) {
  return (
    <Stack spacing={2}>
      <Box sx={{ borderBottom: '1px solid #4f5255' }}>
        <Tabs
          value={tab}
          onChange={(_event, value) => onTabChange(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="PromQL" value="promql" />
          <Tab label="Alerts" value="alerts" />
          <Tab label="Dashboard" value="dashboard" />
          <Tab label="Quick starts" value="quickstarts" />
        </Tabs>
      </Box>
      {tab === 'promql' && (
        <PromqlPanel api={api} cluster={cluster} namespace={namespace} />
      )}
      {tab === 'alerts' && (
        <AlertsPanel api={api} cluster={cluster} namespace={namespace} />
      )}
      {tab === 'dashboard' && (
        <NamespaceDashboard api={api} cluster={cluster} namespace={namespace} />
      )}
      {tab === 'quickstarts' && (
        <QuickStartsPanel
          api={api}
          cluster={cluster}
          namespace={namespace}
          resource={resource}
        />
      )}
    </Stack>
  );
}
